import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { DashboardService } from '../services/dashboard.service';
import { Transcription, isFailedTranscriptionStatus } from '../transcriptions/transcriptions.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    InputTextModule,
    ButtonModule,
    SelectModule,
    TagModule,
    CardModule,
    ProgressSpinnerModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  selectedStatus: string = '';
  statusOptions: any[] = [
    { label: 'All', value: 'All' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Needs Review', value: 'Needs Review' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Failed', value: 'Failed' }
  ];
  searchText: string = '';
  doctorName: string = '';

  
  transcriptions: Transcription[] = [];
  
  keyMetrics: any[] = [];
  keyMetricsMap: { [key: string]: number } = {};
  summaryStatistics: any[] = [];
  summaryStatisticsMap: { [key: string]: number } = {};
  loading = true;
  error = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private dashboardService: DashboardService,
    private auth: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const name = this.auth.getDisplayName();
    this.doctorName = name && name.trim() ? name.trim() : 'Guest';
    // Token extraction is now handled by auth guard before component loads
    this.loadDashboardData();
    
    // Remove focus from any element on page load (only on browser)
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        if (document && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  }

  loadDashboardData() {
    this.loading = true;
    this.error = false;

    // Load transcriptions and calculate metrics/statistics from the data
    this.dashboardService.getTranscriptions().pipe(
      catchError(error => {
        console.error('Error loading transcriptions:', error);
        return of([]);
      })
    ).subscribe({
      next: (transcriptions) => {
        // Process and sort transcriptions
        if (transcriptions && transcriptions.length > 0) {
          this.transcriptions = transcriptions.sort((a, b) => {
            // Use appointmentTime if available, otherwise fall back to date
            // Handle both string and Date types for appointmentTime
            const dateTimeA = a.appointmentTime ? 
              (a.appointmentTime instanceof Date ? a.appointmentTime : new Date(a.appointmentTime)) : 
              new Date(a.date);
            const dateTimeB = b.appointmentTime ? 
              (b.appointmentTime instanceof Date ? b.appointmentTime : new Date(b.appointmentTime)) : 
              new Date(b.date);
            
            return dateTimeB.getTime() - dateTimeA.getTime();
          });
        } else {
          this.transcriptions = [];
        }

        // Calculate key metrics from transcriptions
        this.calculateKeyMetrics(transcriptions);

        // Calculate summary statistics from transcriptions
        this.calculateSummaryStatistics(transcriptions);

        this.loading = false;
        this.error = false; // Only set true on actual request failure (error callback)

        // Trigger change detection to update the view
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.loading = false;
        this.error = true;
        // Trigger change detection even on error
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Calculate key metrics from transcription data
   */
  private calculateKeyMetrics(transcriptions: Transcription[]): void {
    // Use getDisplayStatus logic to normalize statuses
    const statusCounts = transcriptions.reduce((acc, t) => {
      const displayStatus = this.getDisplayStatus(t.status);
      acc[displayStatus] = (acc[displayStatus] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    this.keyMetricsMap = {
      'needs-review': statusCounts['Needs Review'] || 0,
      'in-progress': statusCounts['In Progress'] || 0,
      'completed': statusCounts['Completed'] || 0
    };

    // Also update keyMetrics array for compatibility
    this.keyMetrics = [
      {
        id: 'needs-review',
        number: this.keyMetricsMap['needs-review']
      },
      {
        id: 'in-progress',
        number: this.keyMetricsMap['in-progress']
      },
      {
        id: 'completed',
        number: this.keyMetricsMap['completed']
      }
    ];
  }

  /**
   * Calculate summary statistics from transcription data
   */
  private calculateSummaryStatistics(transcriptions: Transcription[]): void {
    const totalTranscriptions = transcriptions.length;

    // Calculate unique patients
    const uniquePatients = new Set(
      transcriptions
        .map(t => t.patientId || t.mrn)
        .filter(id => id != null && id !== 'N/A' && id !== 'Unknown')
    ).size;

    // Calculate total hours recorded from durations
    // Duration format is typically "HH:MM:SS" or "MM:SS"
    let totalSeconds = 0;
    transcriptions.forEach(t => {
      if (t.duration && t.duration !== 'N/A' && t.duration !== 'Unknown') {
        const parts = t.duration.split(':').map(Number);
        if (parts.length === 3) {
          // HH:MM:SS
          totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
        } else if (parts.length === 2) {
          // MM:SS
          totalSeconds += parts[0] * 60 + parts[1];
        }
      }
    });
    const totalHours = totalSeconds / 3600;

    // Calculate completion rate (percentage of completed transcriptions)
    const completedCount = transcriptions.filter(t => {
      const status = t.status?.toLowerCase() || '';
      return status === 'completed';
    }).length;
    const completionRate = totalTranscriptions > 0 
      ? Math.round((completedCount / totalTranscriptions) * 100 * 10) / 10 
      : 0;

    this.summaryStatisticsMap = {
      'total-transcriptions': totalTranscriptions,
      'patients': uniquePatients,
      'hours-recorded': Math.round(totalHours * 10) / 10,
      'accuracy-rate': completionRate
    };

    // Also update summaryStatistics array for compatibility
    this.summaryStatistics = [
      {
        id: 'total-transcriptions',
        number: totalTranscriptions
      },
      {
        id: 'patients',
        number: uniquePatients
      },
      {
        id: 'hours-recorded',
        number: Math.round(totalHours * 10) / 10
      },
      {
        id: 'accuracy-rate',
        number: completionRate
      }
    ];
  }

  onSearch() {
    // Navigate to transcriptions page with search parameters
    const queryParams: any = {};
    
    // Add search text if provided (searches patient name or MRN)
    if (this.searchText && this.searchText.trim()) {
      queryParams.search = this.searchText.trim();
    }
    
    // Add status filter if selected
    if (this.selectedStatus && this.selectedStatus !== 'All') {
      queryParams.status = this.selectedStatus;
    }
    
    // Navigate to transcriptions page with query parameters
    this.router.navigate(['/v0/transcriptions'], { queryParams });
  }

  /** Full list (no dashboard search filters applied). */
  onViewAllTranscriptions(): void {
    this.router.navigate(['/v0/transcriptions']);
  }

  // Derive title from specialty and appointment type
  getTranscriptionTitle(transcription: Transcription): string {
    if (transcription.specialty) {
      // Format specialty name (e.g., "General Neurology" -> "General Neurology Consultation")
      const specialtyName = transcription.specialty
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return `${specialtyName} Consultation`;
    }
    return 'Consultation';
  }

  // Format date for display
  formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString;
    }
  }

  // Get status severity for PrimeNG tag - same logic as transcriptions component
  getStatusSeverity(status: string): string {
    // Normalize status to handle case-insensitive matching
    const normalizedStatus = status ? status.toLowerCase() : '';
    
    switch (normalizedStatus) {
      case 'completed':
        return 'success';
      case 'needs review':
        return 'warning';
      case 'in progress':
      case 'transcribing':
      case 'transcribed':
      case 'analyzing':
        return 'info';
      case 'paused':
        return 'secondary';
      case 'failed':
        return 'danger';
      default:
        // Treat any unknown status as "in progress" for styling
        return 'info';
    }
  }

  // Get display status - same logic as transcriptions component
  getDisplayStatus(status: string): string {
    // Normalize status to handle case variations from backend
    const normalizedStatus = status ? status.toLowerCase() : '';
    
    // Map backend statuses to display-friendly names
    // Only valid statuses: Completed, Needs Review, In Progress, Failed
    switch (normalizedStatus) {
      case 'completed':
        return 'Completed';
      case 'needs review':
        return 'Needs Review';
      case 'in progress':
      case 'transcribing':
      case 'transcribed':
      case 'analyzing':
        return 'In Progress';
      case 'paused':
        return 'Paused';
      case 'failed':
        return 'Failed';
      default:
        // Treat any unknown status as "In Progress"
        return 'In Progress';
    }
  }

  isPausedTranscription(transcription: Transcription): boolean {
    return this.getDisplayStatus(transcription.status) === 'Paused';
  }

  /** True when the transcription is still processing — detail view is not available from the dashboard. */
  isTranscriptionInProgress(transcription: Transcription): boolean {
    if (this.isPausedTranscription(transcription)) {
      return false;
    }
    return this.getDisplayStatus(transcription.status) === 'In Progress';
  }

  recentTranscriptionButtonLabel(transcription: Transcription): string {
    if (this.isPausedTranscription(transcription)) {
      return 'Resume';
    }
    return this.isTranscriptionInProgress(transcription) ? 'Processing' : 'View';
  }

  /** Resume paused visit on recording page, or open detail when terminal. */
  onRecentTranscriptionAction(transcription: Transcription): void {
    if (this.isPausedTranscription(transcription)) {
      void this.router.navigate(['/v0/recording'], {
        queryParams: { resumeTranscription: transcription.id }
      });
      return;
    }
    this.onViewTranscription(transcription);
  }

  // Navigate to transcription detail (blocked while in progress)
  onViewTranscription(transcription: Transcription): void {
    if (this.isTranscriptionInProgress(transcription) || isFailedTranscriptionStatus(transcription.status)) {
      return;
    }
    this.router.navigate(['/v0/transcriptions', transcription.id]);
  }

  // Helper method to check if content should be displayed (metrics/summary cards only when we have data)
  get shouldShowContent(): boolean {
    return !this.loading && !this.error && this.transcriptions.length > 0;
  }

} 